import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { EditableGrid } from "../../components/DataTable/EditableGrid";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { useAppDispatch } from "../../store/hooks";
import {
  createProductPricesData,
  fetchProductPriceUnits,
  updateProductPricesData,
} from "../../slices/productcostapplication.slice";
import { validatePricePeriodsGridData } from "../Modals/action-bill-modal/util";
import { v4 as uuidv4 } from "uuid";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { ActionCell } from "../../components/DataTable/customcell/ActionCell";

interface Props {
  isEdit: boolean;
  open: boolean;
  handleClose: () => void;
  product: any;
}

const types = ["Fertilizer", "Herbicide"];
const priceUnits = ["$/gal", "$/lb", "$/lbn" , "$/oz"];

const newRow = {
  price: "",
  purchasedQuantity: null,
  purchasedQuantityUnitId: null,
  notes: "",
  firstAppliedDate: new Date(),
  lastAppliedDate: new Date(),
};

export const AddEditProductPriceModal = ({
  isEdit,
  handleClose,
  open,
  product,
}: Props) => {
  const [type, setType] = useState("");
  const [priceUnit, setPriceUnit] = useState("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});
  const productNameRef = useRef("");
  const [isProductNameValid, setIsProductNameValid] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchProductPriceUnits());
  }, [dispatch]);

  useEffect(() => {
    if (product) {
      const { name, type, priceUnit, periods } = product;
      productNameRef.current = name;
      setIsProductNameValid(!!name);
      setType(type);
      setPriceUnit(priceUnit);
      setTableData(
        periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            firstAppliedDate: moment(
              item.firstAppliedDate,
              "MM/DD/YYYY"
            ).toDate(),
            lastAppliedDate: moment(
              item.lastAppliedDate,
              "MM/DD/YYYY"
            ).toDate(),
          };
        })
      );
    }
  }, [product]);

  const addNewPeriod = () => {
    setTableData((prev) => [...prev, { ...newRow, id: uuidv4() }]);
  };

  const onItemChange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = {
          ...item,
        };
        updatedRow[field] = value;
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  useEffect(() => {
    setErrorFields(
      validatePricePeriodsGridData(tableData, {
        price: "Price",
        firstAppliedDate: "First Applied",
        lastAppliedDate: "Last Applied",
        type: "Type",
      })
    );
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  const handleSave = async () => {
    const requestData = {
      productName: productNameRef.current,
      priceUnit: priceUnit,
      ProductType: type,
      periods: tableData.map((item: any) => {
        return {
          firstAppliedDate: moment(item.firstAppliedDate).format("MM/DD/YYYY"),
          lastAppliedDate: moment(item.lastAppliedDate).format("MM/DD/YYYY"),
          price: item.price,
          notes: item.notes,
        };
      }),
    };
    if (isEdit) {
      await dispatch(
        updateProductPricesData({ ...requestData, productId: product.id })
      );
    } else {
      await dispatch(createProductPricesData(requestData));
    }
    handleClose();
  };

  const deletePricePeriod = (dataItem: any) => {
    setTableData(tableData.filter((item: any) => item.id !== dataItem.id));
  };

  const handleProductNameChange = (event: any) => {
    productNameRef.current = event.target.value;
  };

  const handleProductNameBlur = (event: any) => {
    const value = event.target.value;
    setIsProductNameValid(!!value);
  };

  return (
    <BaseModal open={open} handleClose={handleClose}>
      <Box display={"flex"} flexDirection={"column"} gap={5}>
        <Typography variant="h6">
          {isEdit ? "Edit Product" : "Add Product"}
        </Typography>
        <Box display={"flex"} flexWrap={"wrap"} gap={2}>
          <TextField
            label="Product Name"
            variant="outlined"
            defaultValue={product?.name ?? ""}
            onChange={handleProductNameChange}
            onBlur={handleProductNameBlur}
          />
          <FormControl>
            <InputLabel>Type</InputLabel>
            <Select
              style={{ minWidth: 200 }}
              value={type}
              label="Type"
              onChange={(event: any) => setType(event.target.value)}
            >
              {types.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Price Unit</InputLabel>
            <Select
              value={priceUnit}
              style={{ minWidth: 200 }}
              label="Price Unit"
              onChange={(event: any) => setPriceUnit(event.target.value)}
            >
              {priceUnits.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            mb={1}
            alignItems={"center"}
          >
            <Typography>Price Periods</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={addNewPeriod}
            >
              Add Period
            </Button>
          </Box>
          <EditableGrid
            dataItemKey="id"
            onItemChange={onItemChange}
            edit={edit}
            columns={[
              {
                field: "price",
                title: "Price",
                editor: "numeric",
              },
              {
                field: "firstAppliedDate",
                title: "First Applied",
                editor: "date",
                format: "{0:d}"
              },
              {
                field: "lastAppliedDate",
                title: "Last Applied",
                editor: "date",
                format: "{0:d}"
              },
              {
                field: "notes",
                title: "Notes",
              },
              {
                title: "",
                cells: {
                  data: (props) => (
                    <ActionCell {...props} onDelete={deletePricePeriod} />
                  ),
                },
              },
            ]}
            data={tableData}
          />
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              !(
                isProductNameValid &&
                type &&
                priceUnit &&
                tableData.length &&
                !errorFields.length
              )
            }
            onClick={handleSave}
          >
            Save
          </Button>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
